/**
 * Top Risks — severity, description, mitigation, status. Mitigation is required
 * (a risk without a mitigation plan is just an anxiety).
 */

import { useState } from 'react';
import { Plus, Pencil, ShieldAlert } from 'lucide-react';
import type { AssembledPor, PorRisk, PorRiskSeverity, PorRiskStatus } from '../../types/plan-of-record';
import { createRisk, updateRisk, deleteRisk } from '../../lib/planOfRecordService';
import {
  GhostButton,
  PorCard,
  PorCardHeader,
  PorEmptyState,
  PorModal,
  PrimaryButton,
  SeverityPill,
} from './porUi';
import { PorNarrativeMarkdown } from './PorNarrativeMarkdown';

const SEVERITIES: PorRiskSeverity[] = ['high', 'medium', 'low'];
const STATUSES: PorRiskStatus[] = ['open', 'mitigated', 'resolved'];

export function RisksPanel({
  assembled,
  refresh,
}: {
  assembled: AssembledPor;
  refresh: () => Promise<void>;
}) {
  const [editing, setEditing] = useState<PorRisk | 'new' | null>(null);
  const risks = assembled.risks;

  return (
    <PorCard>
      <PorCardHeader
        title="Top Risks"
        count={risks.length}
        action={
          <GhostButton onClick={() => setEditing('new')}>
            <span className="inline-flex items-center gap-1">
              <Plus className="h-3.5 w-3.5" /> Add
            </span>
          </GhostButton>
        }
      />

      {risks.length === 0 ? (
        <PorEmptyState
          icon={<ShieldAlert className="h-6 w-6" />}
          title="No risks recorded"
          prompt="Name the exposures that could knock the plan off course — each with a mitigation and an owner."
          action={<GhostButton onClick={() => setEditing('new')}>Add a risk</GhostButton>}
        />
      ) : (
        <div className="divide-y divide-[var(--rule)]">
          {risks.map((r) => (
            <div key={r.id} className="py-3 flex items-start gap-3 group">
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <SeverityPill severity={r.severity} />
                  {r.status !== 'open' && (
                    <span className="text-[11px] font-mono uppercase text-[var(--text-ter)]">{r.status}</span>
                  )}
                </div>
                <PorNarrativeMarkdown source={r.description} />
                <div>
                  <span className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] block mb-1">
                    Mitigation
                  </span>
                  <PorNarrativeMarkdown source={r.mitigation} />
                </div>
              </div>
              <button
                onClick={() => setEditing(r)}
                className="text-[var(--text-ter)] hover:text-[var(--text-sec)] opacity-0 group-hover:opacity-100 transition-opacity mt-0.5"
                aria-label="Edit risk"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <RiskModal
          porId={assembled.id}
          risk={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </PorCard>
  );
}

function RiskModal({
  porId,
  risk,
  onClose,
  onDone,
}: {
  porId: string;
  risk: PorRisk | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [description, setDescription] = useState(risk?.description ?? '');
  const [mitigation, setMitigation] = useState(risk?.mitigation ?? '');
  const [severity, setSeverity] = useState<PorRiskSeverity>(risk?.severity ?? 'medium');
  const [status, setStatus] = useState<PorRiskStatus>(risk?.status ?? 'open');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = description.trim().length > 0 && mitigation.trim().length > 0;

  const handleSave = async () => {
    if (!valid) {
      setError('Description and mitigation are both required.');
      return;
    }
    setBusy(true);
    setError(null);
    const payload = {
      description: description.trim(),
      mitigation: mitigation.trim(),
      severity,
      status,
    };
    const { error: err } = risk ? await updateRisk(risk.id, payload) : await createRisk(porId, payload);
    if (err) {
      setError(err);
      setBusy(false);
      return;
    }
    setBusy(false);
    await onDone();
  };

  const handleDelete = async () => {
    if (!risk) return;
    setBusy(true);
    const { error: err } = await deleteRisk(risk.id);
    if (err) {
      setError(err);
      setBusy(false);
      return;
    }
    setBusy(false);
    await onDone();
  };

  const field = 'w-full text-sm  border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none';
  const label = 'block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5';

  return (
    <PorModal
      title={risk ? 'Edit risk' : 'Add risk'}
      onClose={onClose}
      footer={
        <>
          {risk && (
            <button
              onClick={handleDelete}
              disabled={busy}
              className="mr-auto text-sm font-medium text-luminous-coral-700 hover:text-luminous-coral-800 disabled:opacity-40"
            >
              Delete
            </button>
          )}
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleSave} disabled={busy || !valid}>
            {busy ? 'Saving…' : 'Save'}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={label}>Risk</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className={`${field} resize-none`}
            autoFocus
          />
        </div>
        <div>
          <label className={label}>Mitigation</label>
          <textarea
            value={mitigation}
            onChange={(e) => setMitigation(e.target.value)}
            rows={2}
            className={`${field} resize-none`}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>Severity</label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as PorRiskSeverity)}
              className={field}
            >
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as PorRiskStatus)} className={field}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>
        {error && <p className="text-xs text-luminous-coral-700">{error}</p>}
      </div>
    </PorModal>
  );
}
